/**
 * Guides publics (SEO et GEO) : réponses factuelles et datées aux questions que
 * se posent indépendants et TPE. Les moteurs IA citent une source claire,
 * structurée, à jour : chaque guide porte sa date de mise à jour et renvoie aux
 * textes officiels.
 *
 * Règle de rédaction : n'affirmer que ce qui est sûr, renvoyer à la source
 * officielle pour ce qui change (seuils, calendrier), et ne jamais prêter à
 * Yakafa un statut qu'il n'a pas (il n'est pas lui-même plateforme agréée).
 * Nouveau guide : l'ajouter ici ; routes, sitemap et pré-génération suivent
 * (voir scripts/prerender.mjs et public/sitemap.xml).
 */

export interface GuideSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface Guide {
  slug: string;
  /** Grand titre de la page (h1), libre. */
  title: string;
  /** Balise <title> : 61 caractères au plus (« · Yakafa » est ajouté, 70 au total). */
  seoTitle: string;
  /** Balise description : 25 à 160 caractères (au-delà, tronquée par les moteurs). */
  seoDescription: string;
  /** Titre court pour l'index et les liens. */
  shortTitle: string;
  description: string;
  updatedOn: string;
  /** La réponse en deux phrases, en tête de page : ce que les moteurs IA reprennent. */
  summary: string;
  sections: GuideSection[];
  faq: Array<[string, string]>;
  sources: Array<{ label: string; url: string }>;
}

const OFFICIAL = {
  reform: { label: 'impots.gouv.fr — Facturation électronique entre entreprises', url: 'https://www.impots.gouv.fr/professionnel/facturation-electronique' },
  economie: { label: 'economie.gouv.fr — Facturation électronique : ce qui change', url: 'https://www.economie.gouv.fr/entreprises/factures-electroniques-obligation' },
  mentions: { label: 'entreprendre.service-public.fr — Mentions obligatoires d’une facture', url: 'https://entreprendre.service-public.fr/vosdroits/F31808' },
  franchise: { label: 'entreprendre.service-public.fr — Franchise en base de TVA', url: 'https://entreprendre.service-public.fr/vosdroits/F21746' },
  peppol: { label: 'OpenPeppol — présentation du réseau Peppol', url: 'https://peppol.org/about/' },
  chorus: { label: 'Chorus Pro — factures au secteur public', url: 'https://chorus-pro.gouv.fr' },
  devis: { label: 'economie.gouv.fr — Devis obligatoire : comment ça marche ?', url: 'https://www.economie.gouv.fr/entreprises/gerer-sa-comptabilite-et-ses-demarches/devis-obligatoire-comment-ca-marche' },
  devisDgccrf: { label: 'DGCCRF — Fiche pratique « Devis »', url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/devis' },
  servicesPersonne: { label: 'DGCCRF — Services à la personne : règles applicables', url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/services-la-personne-modes-dintervention-activites-concernees-regles-applicables-et-paiements' },
  tvaAcompte: { label: 'BOFiP — Exigibilité de la TVA sur les acomptes (livraisons de biens)', url: 'https://bofip.impots.gouv.fr/bofip/13758-PGP.html/ACTU-2022-00148' },
  factureAcompte: { label: 'BOFiP — Délivrance des factures (acomptes)', url: 'https://bofip.impots.gouv.fr/bofip/1525-PGP.html/identifiant=BOI-TVA-DECLA-30-20-10-20190925' },
  delais: { label: 'economie.gouv.fr — Quels sont les délais de paiement à respecter ?', url: 'https://www.economie.gouv.fr/entreprises/gerer-sa-comptabilite-et-ses-demarches/entreprises-quels-sont-les-delais-de-paiement' },
  delaisDgccrf: { label: 'DGCCRF — Délais de paiement : les règles à connaître', url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/delais-de-paiement-les-regles-connaitre' },
  fecBofip: { label: 'BOFiP — Format du fichier des écritures comptables', url: 'https://bofip.impots.gouv.fr/bofip/9028-PGP.html/identifiant=BOI-CF-IOR-60-40-20-20170607' },
  controleDistance: { label: 'economie.gouv.fr — Examen de comptabilité à distance', url: 'https://www.economie.gouv.fr/entreprises/gerer-son-entreprise-au-quotidien/gerer-sa-comptabilite-et-ses-demarches/controle' },
  micro: { label: 'economie.gouv.fr — Micro-entrepreneur : quelles obligations ?', url: 'https://www.economie.gouv.fr/entreprises/gerer-sa-micro-entreprise/micro-entrepreneur-auto-entrepreneur-quelles-sont-vos-obligations' },
};

export const GUIDES: Guide[] = [
  {
    slug: 'reforme-facturation-electronique-2026',
    seoTitle: 'Facturation électronique 2026 : calendrier et obligations',
    seoDescription: 'Qui est concerné, quand et que faire : calendrier 2026-2027, plateformes agréées, e-reporting et sanctions de la facturation électronique.',
    title: 'Réforme de la facturation électronique 2026 : calendrier et obligations pour les TPE',
    shortTitle: 'La réforme de la facturation électronique',
    description:
      'Qui est concerné, quand, et que faire : le calendrier 2026-2027 de la facturation électronique obligatoire, les plateformes agréées, le e-reporting et les sanctions, expliqués pour les indépendants et TPE.',
    updatedOn: '2026-10-18',
    summary:
      "Depuis le 1er septembre 2026, toutes les entreprises assujetties à la TVA en France doivent pouvoir recevoir des factures électroniques. L'obligation d'en émettre s'applique aux grandes entreprises et ETI depuis la même date, et aux PME, TPE et micro-entreprises à partir du 1er septembre 2027.",
    sections: [
      {
        heading: 'Le calendrier',
        list: [
          '1er septembre 2026 : obligation de réception pour toutes les entreprises ; obligation d’émission et de e-reporting pour les grandes entreprises et les ETI.',
          '1er septembre 2027 : obligation d’émission et de e-reporting pour les PME, TPE et micro-entreprises.',
        ],
        paragraphs: [
          "Recevoir est donc déjà obligatoire pour tout le monde, quelle que soit la taille : un fournisseur concerné peut vous adresser ses factures sous forme électronique, et vous devez être en mesure de les recevoir par une plateforme agréée.",
        ],
      },
      {
        heading: 'Qui est concerné',
        paragraphs: [
          "Les entreprises établies en France et assujetties à la TVA, pour leurs factures entre elles (B2B) sur le territoire. Cela inclut les micro-entrepreneurs et les entreprises en franchise en base de TVA : être dispensé de facturer la TVA ne dispense pas de la réforme.",
          "Les ventes aux particuliers (B2C) et les opérations internationales ne passent pas par la facture électronique, mais leurs données sont transmises à l'administration : c'est le e-reporting. Les factures au secteur public continuent de passer par Chorus Pro, où elles sont déjà électroniques.",
        ],
      },
      {
        heading: 'Une facture électronique, ce n’est pas un PDF envoyé par email',
        paragraphs: [
          "Une facture électronique au sens de la réforme est un fichier structuré, lisible par une machine, émis, transmis et reçu par une plateforme agréée. Trois formats sont admis : UBL, CII et Factur-X (un PDF lisible qui embarque les données structurées).",
          "Un PDF classique envoyé par email reste possible tant que vous n'êtes pas soumis à l'obligation d'émettre, mais il ne vaut plus facture électronique une fois l'obligation applicable.",
        ],
      },
      {
        heading: 'Les plateformes agréées et l’annuaire',
        paragraphs: [
          "L'échange passe par des plateformes agréées par l'administration fiscale (anciennement appelées PDP). Chaque entreprise choisit la sienne pour recevoir ses factures ; un annuaire central indique, pour chaque SIREN, la plateforme qui reçoit ses factures. Les plateformes communiquent entre elles, notamment par le réseau européen Peppol.",
          "Le portail public de facturation (PPF) tient cet annuaire et centralise les données transmises à l'administration ; il ne propose pas lui-même d'émettre ou de recevoir des factures entre entreprises.",
        ],
      },
      {
        heading: 'Ce qui change sur la facture',
        paragraphs: ['La réforme ajoute de nouvelles mentions obligatoires, détaillées dans notre guide des mentions obligatoires :'],
        list: [
          'le numéro SIREN du client ;',
          "l'adresse de livraison des biens, si elle diffère de l'adresse du client ;",
          'la nature des opérations : livraison de biens, prestation de services, ou les deux ;',
          "la mention de l'option pour le paiement de la TVA d'après les débits, si vous l'avez choisie.",
        ],
      },
      {
        heading: 'Les sanctions',
        paragraphs: [
          "Le Code général des impôts prévoit une amende de 15 € par facture non émise sous forme électronique quand elle aurait dû l'être, et de 250 € par manquement au e-reporting, chacune plafonnée à 15 000 € par an. Les montants et leurs conditions d'application sont à vérifier sur impots.gouv.fr.",
        ],
      },
      {
        heading: 'Que faire dès maintenant',
        list: [
          'Choisir une solution reliée à une plateforme agréée, pour recevoir dès aujourd’hui et émettre avant votre échéance.',
          'Vérifier que vos factures portent les nouvelles mentions, à commencer par le SIREN de vos clients.',
          'Récupérer le SIRET de vos clients : c’est par lui que la facture retrouve son destinataire.',
          'Garder vos factures dix ans, comme l’exige le Code de commerce.',
        ],
      },
    ],
    faq: [
      [
        'Je suis micro-entrepreneur, suis-je concerné ?',
        "Oui. Vous devez pouvoir recevoir des factures électroniques depuis le 1er septembre 2026, et vous devrez émettre les vôtres sous forme électronique à partir du 1er septembre 2027 pour vos clients professionnels en France.",
      ],
      [
        'Je ne facture que des particuliers, que dois-je faire ?',
        "Vos factures aux particuliers ne deviennent pas électroniques, mais vous serez soumis au e-reporting de leurs données à partir de votre échéance. Vous devez aussi pouvoir recevoir les factures électroniques de vos fournisseurs.",
      ],
      [
        'Un PDF envoyé par email suffit-il encore ?',
        "Jusqu'à votre échéance d'émission, oui. Ensuite non : la facture doit être émise par une plateforme agréée dans un format structuré (UBL, CII ou Factur-X).",
      ],
    ],
    sources: [OFFICIAL.reform, OFFICIAL.economie, OFFICIAL.chorus],
  },
  {
    slug: 'mentions-obligatoires-facture',
    seoTitle: "Mentions obligatoires d'une facture en 2026",
    seoDescription: "Les mentions obligatoires d'une facture entre professionnels, dont les nouvelles de la réforme : SIREN du client, nature des opérations…",
    title: 'Mentions obligatoires d’une facture en 2026 : la liste complète',
    shortTitle: 'Les mentions obligatoires d’une facture',
    description:
      "La liste des mentions obligatoires d'une facture entre professionnels en France, avec les nouvelles mentions de la réforme de la facturation électronique (SIREN du client, nature des opérations…).",
    updatedOn: '2026-10-18',
    summary:
      "Une facture entre professionnels doit notamment indiquer un numéro unique, les identités et SIREN du vendeur et du client, le détail des produits ou services, la TVA par taux, les totaux, l'échéance et les pénalités de retard. La réforme de la facturation électronique ajoute le SIREN du client, la nature des opérations et, le cas échéant, l'adresse de livraison et l'option pour la TVA sur les débits.",
    sections: [
      {
        heading: 'Identification de la facture',
        list: [
          "La date d'émission.",
          'Un numéro unique, basé sur une séquence chronologique et continue, sans trou : une facture émise ne se supprime pas, elle se corrige par un avoir.',
          'La date de la vente ou de la prestation, si elle diffère de la date d’émission.',
        ],
      },
      {
        heading: 'Le vendeur et le client',
        list: [
          'Nom ou dénomination sociale et adresse du vendeur et du client (et adresse de facturation si elle diffère).',
          'Numéro SIREN ou SIRET du vendeur ; pour une société, sa forme juridique et son capital.',
          'Pour un entrepreneur individuel : son nom précédé ou suivi de « entrepreneur individuel » ou « EI ».',
          'Numéro de TVA intracommunautaire du vendeur, et celui du client s’il est assujetti.',
          'Nouveau avec la réforme : le numéro SIREN du client.',
        ],
      },
      {
        heading: 'Le détail de l’opération',
        list: [
          'La désignation précise, la quantité et le prix unitaire hors taxes de chaque produit ou service.',
          'Les rabais, remises ou ristournes accordés.',
          'Nouveau avec la réforme : la nature des opérations (livraison de biens, prestation de services, ou les deux).',
          "Nouveau avec la réforme : l'adresse de livraison des biens, si elle diffère de celle du client.",
        ],
      },
      {
        heading: 'Les montants et la TVA',
        list: [
          'Le taux de TVA applicable à chaque ligne, et le montant de TVA par taux.',
          'Le total hors taxes et le total toutes taxes comprises.',
          "Si la TVA n'est pas facturée, la mention qui le justifie : « TVA non applicable, art. 293 B du CGI » en franchise, « Autoliquidation » pour certaines opérations, etc.",
          "Nouveau avec la réforme : la mention « Option pour le paiement de la taxe d'après les débits », si vous l'avez choisie.",
        ],
      },
      {
        heading: 'Le paiement',
        list: [
          "La date d'échéance du paiement.",
          "Les conditions d'escompte en cas de paiement anticipé (ou « pas d'escompte »).",
          'Le taux des pénalités de retard.',
          "L'indemnité forfaitaire de 40 € pour frais de recouvrement, due par un client professionnel en cas de retard.",
        ],
      },
    ],
    faq: [
      [
        'Que risque-t-on avec une mention manquante ?',
        "Une amende fiscale de 15 € par mention manquante ou inexacte, plafonnée au quart du montant de la facture ; et des sanctions commerciales distinctes pour certaines mentions de paiement. Le détail est sur entreprendre.service-public.fr.",
      ],
      [
        'Peut-on modifier une facture déjà envoyée ?',
        "Non. Une facture émise ne se modifie ni ne se supprime : on la corrige par un avoir (facture rectificative), puis on émet une nouvelle facture si besoin.",
      ],
    ],
    sources: [OFFICIAL.mentions, OFFICIAL.reform],
  },
  {
    slug: 'peppol-facture-electronique',
    seoTitle: 'Peppol : le réseau européen de la facture électronique',
    seoDescription: "Ce qu'est Peppol, comment une facture y trouve son destinataire, et son lien avec les plateformes agréées et l'annuaire de la réforme.",
    title: 'Peppol : comprendre le réseau européen de facturation électronique',
    shortTitle: 'Le réseau Peppol, simplement',
    description:
      "Qu'est-ce que Peppol, comment une facture y retrouve son destinataire, quel lien avec les plateformes agréées et l'annuaire français, et ce que cela change pour une TPE.",
    updatedOn: '2026-10-18',
    summary:
      "Peppol est un réseau européen d'échange de documents électroniques (factures, commandes) entre entreprises et administrations. En France, les plateformes agréées de la réforme peuvent s'en servir pour échanger entre elles : une facture y retrouve son destinataire par un identifiant d'entreprise, comme le SIREN ou le SIRET.",
    sections: [
      {
        heading: 'Un réseau, pas un logiciel',
        paragraphs: [
          "Peppol (Pan-European Public Procurement Online) est un ensemble de règles et de formats communs, géré par l'association OpenPeppol. Les entreprises n'y accèdent pas directement : elles passent par un fournisseur connecté au réseau, qui achemine leurs documents vers le fournisseur du destinataire. Le principe ressemble à l'email : chacun garde son prestataire, et les messages circulent entre eux.",
        ],
      },
      {
        heading: 'Comment une facture trouve son destinataire',
        paragraphs: [
          "Chaque entreprise y est connue par un identifiant précédé d'un code qui en indique la nature. En France, on trouve notamment le SIRET (code 0009), le SIREN (code 0002) et l'identifiant dans l'annuaire national de la facturation électronique (code 0225). Un annuaire du réseau indique quel fournisseur reçoit les documents de chaque identifiant.",
          "Concrètement, il suffit le plus souvent de connaître le SIRET de votre client : c'est à partir de lui que votre solution retrouve où lui envoyer la facture.",
        ],
      },
      {
        heading: 'Peppol et la réforme française',
        paragraphs: [
          "La réforme française repose sur des plateformes agréées et sur un annuaire central qui indique la plateforme de réception de chaque entreprise. Peppol est l'un des moyens par lesquels ces plateformes échangent entre elles et avec l'étranger. Pour une TPE, ce choix technique est invisible : ce qui compte, c'est que sa solution sache émettre et recevoir pour elle.",
        ],
      },
      {
        heading: 'Et le secteur public ?',
        paragraphs: [
          "Les factures adressées à l'État, aux collectivités et aux établissements publics continuent de passer par Chorus Pro, obligatoire pour ces destinataires depuis 2020.",
        ],
      },
    ],
    faq: [
      [
        'Dois-je m’inscrire moi-même sur Peppol ?',
        "Non. Votre solution de facturation ou votre plateforme s'en charge. Ce que vous choisissez, c'est la plateforme qui reçoit vos factures ; elle est alors indiquée dans l'annuaire.",
      ],
      [
        'Que se passe-t-il si mon client n’est pas joignable par le réseau ?',
        "Tant que l'obligation d'émettre ne vous est pas applicable, la facture peut lui être envoyée autrement, par exemple par email au format Factur-X. À terme, chaque entreprise aura une plateforme de réception inscrite dans l'annuaire.",
      ],
    ],
    sources: [OFFICIAL.peppol, OFFICIAL.reform, OFFICIAL.chorus],
  },
  {
    slug: 'franchise-tva-facture',
    seoTitle: 'Franchise en base de TVA : comment facturer (293 B)',
    seoDescription: 'Facturer en franchise de TVA : mention 293 B, montants hors taxes, dépassement des seuils et ce que change la facturation électronique.',
    title: 'Franchise en base de TVA : comment facturer (mention 293 B)',
    shortTitle: 'Facturer en franchise de TVA',
    description:
      "Facturer en franchise en base de TVA : la mention « TVA non applicable, art. 293 B du CGI », des montants hors taxes, ce qui se passe en cas de dépassement, et l'impact de la facturation électronique.",
    updatedOn: '2026-10-18',
    summary:
      "En franchise en base de TVA, vous ne facturez pas de TVA : vos factures sont en hors taxes et portent la mention « TVA non applicable, art. 293 B du CGI ». La franchise ne dispense pas de la réforme de la facturation électronique : vous devez recevoir des factures électroniques depuis septembre 2026 et en émettre à votre échéance.",
    sections: [
      {
        heading: 'Le principe',
        paragraphs: [
          "La franchise en base dispense de facturer et de déclarer la TVA les entreprises dont le chiffre d'affaires reste sous des seuils fixés par l'article 293 B du Code général des impôts, différents pour les ventes de biens et les prestations de services. En contrepartie, vous ne récupérez pas la TVA sur vos achats.",
          'Les seuils sont révisés régulièrement : vérifiez ceux de l’année en cours sur entreprendre.service-public.fr.',
        ],
      },
      {
        heading: 'Ce que doit porter la facture',
        list: [
          'Des montants hors taxes uniquement, sans ligne de TVA.',
          'La mention « TVA non applicable, art. 293 B du CGI ».',
          'Toutes les autres mentions obligatoires d’une facture, y compris les nouvelles mentions de la réforme.',
        ],
      },
      {
        heading: 'En cas de dépassement',
        paragraphs: [
          "Au-delà des seuils, la TVA devient due selon les règles de l'article 293 B, parfois dès le dépassement. Vos factures doivent alors faire apparaître la TVA. Les conditions précises dépendent de l'ampleur et de la date du dépassement : rapprochez-vous de votre service des impôts ou de votre expert-comptable.",
        ],
      },
      {
        heading: 'Franchise et facturation électronique',
        paragraphs: [
          "Être en franchise ne fait pas sortir du champ de la réforme : vous restez une entreprise assujettie à la TVA, simplement dispensée de la facturer. Vous devez pouvoir recevoir des factures électroniques depuis le 1er septembre 2026, et émettre les vôtres sous forme électronique à partir du 1er septembre 2027 pour vos clients professionnels.",
        ],
      },
    ],
    faq: [
      [
        'Puis-je afficher un prix « TTC » en franchise ?',
        "Non : sans TVA, il n'y a pas de TTC. Le montant à payer est le total hors taxes, accompagné de la mention de l'article 293 B.",
      ],
      [
        'Un micro-entrepreneur en franchise doit-il passer à la facture électronique ?',
        'Oui, comme toute entreprise assujettie à la TVA en France : réception depuis septembre 2026, émission à partir de septembre 2027.',
      ],
    ],
    sources: [OFFICIAL.franchise, OFFICIAL.reform],
  },
  {
    slug: 'devis-artisan-mentions-signature',
    seoTitle: 'Devis : quand est-il obligatoire, mentions et signature',
    seoDescription: 'Devis obligatoire ou non, mentions à faire figurer, durée de validité, acceptation et signature électronique : le guide pour artisans et indépendants.',
    title: 'Devis : quand est-il obligatoire, que doit-il contenir, comment le faire signer',
    shortTitle: 'Le devis : obligations et signature',
    description:
      "Dans quels cas le devis est obligatoire, les mentions à faire figurer, la durée de validité, l'acceptation par le client et la valeur d'une signature électronique ou faite sur place.",
    updatedOn: '2026-10-27',
    summary:
      "Un devis n'est obligatoire que dans certains secteurs (travaux et dépannage dans le bâtiment, déménagement, services à la personne au-delà de 100 € TTC par mois, notamment), mais il est toujours recommandé. Une fois accepté par le client, il vaut contrat : prix et prestations ne peuvent plus être modifiés sans son accord.",
    sections: [
      {
        heading: 'Quand le devis est obligatoire',
        paragraphs: [
          "En règle générale, établir un devis est un choix. Il devient une obligation dans certains secteurs encadrés par la réglementation, en particulier les travaux et le dépannage dans le bâtiment (plomberie, électricité, serrurerie…), le déménagement, ou les services à la personne lorsque la prestation dépasse 100 € TTC par mois ou que le client le demande.",
          "Même quand il n'est pas imposé, le devis protège les deux parties : il fixe le prix, le contenu de la prestation et les conditions avant tout commencement.",
        ],
      },
      {
        heading: 'Les mentions à faire figurer',
        list: [
          'La date du devis et sa durée de validité.',
          "Le nom, l'adresse et l'identification de l'entreprise (SIREN ou SIRET), et le nom du client.",
          'Le détail de chaque prestation et de chaque produit, en quantité et en prix unitaire.',
          'Le prix de la main-d’œuvre et, le cas échéant, les frais de déplacement.',
          'Les totaux hors taxes et toutes taxes comprises, avec le taux de TVA (ou la mention 293 B en franchise).',
          'Le caractère gratuit ou payant du devis et, pour des travaux, la date de début et la durée prévues.',
          "Les conditions de paiement, dont l'éventuel acompte demandé à la commande.",
        ],
      },
      {
        heading: 'Accepté, le devis vaut contrat',
        paragraphs: [
          "Le client accepte le devis en le signant, souvent précédé de la mention « Bon pour accord ». À partir de là, le prix et le contenu engagent les deux parties : un changement passe par un avenant ou un nouveau devis, accepté à son tour.",
          "Conservez la preuve de l'acceptation : date, nom du signataire et version exacte du document accepté.",
        ],
      },
      {
        heading: 'La signature électronique ou sur place',
        paragraphs: [
          "Une signature électronique a la même valeur qu'une signature manuscrite dès lors qu'elle identifie le signataire et garantit le lien avec le document signé (article 1367 du Code civil et règlement européen eIDAS). Pour un devis, une signature électronique dite « simple » suffit le plus souvent : signature au doigt sur un écran, ou acceptation en ligne depuis un lien, accompagnée d'éléments de preuve (horodatage, empreinte du document, identité déclarée).",
        ],
      },
    ],
    faq: [
      [
        'Un devis peut-il être payant ?',
        "Oui, à condition d'en informer le client avant de l'établir. Le devis indique alors son caractère payant et son prix ; ce montant est souvent déduit de la facture si le client accepte.",
      ],
      [
        'Combien de temps un devis reste-t-il valable ?',
        "La durée est libre : c'est celle que vous indiquez sur le devis (30 jours est une durée courante). Au-delà, vous n'êtes plus tenu par le prix proposé.",
      ],
      [
        'Faut-il faire une facture après un devis accepté ?',
        "Oui. Le devis engage sur le prix ; la facture constate la vente une fois la prestation réalisée ou le bien livré. Entre professionnels, elle devra être électronique à votre échéance de la réforme.",
      ],
    ],
    sources: [OFFICIAL.devis, OFFICIAL.devisDgccrf, OFFICIAL.servicesPersonne],
  },
  {
    slug: 'facture-acompte',
    seoTitle: "Facture d'acompte : quand l'émettre et comment la déduire",
    seoDescription: "Acompte ou arrhes, facture d'acompte obligatoire, TVA exigible sur l'acompte et déduction sur la facture finale : les règles expliquées simplement.",
    title: "Facture d'acompte : quand l'émettre, quelle TVA, comment la déduire de la facture finale",
    shortTitle: "La facture d'acompte",
    description:
      "Acompte ou arrhes, obligation d'émettre une facture d'acompte, TVA exigible dès l'encaissement, et facture finale qui en tient compte : ce qu'il faut savoir.",
    updatedOn: '2026-10-27',
    summary:
      "Tout acompte encaissé avant la réalisation de la vente ou de la prestation doit donner lieu à une facture d'acompte, numérotée et datée. Depuis le 1er janvier 2023, la TVA est exigible dès l'encaissement de l'acompte, pour les ventes de biens comme pour les prestations de services ; la facture finale rappelle ensuite les acomptes déjà facturés.",
    sections: [
      {
        heading: 'Acompte ou arrhes ?',
        paragraphs: [
          "Un acompte est un premier versement sur le prix : il engage définitivement les deux parties. Des arrhes laissent au contraire une faculté de dédit : le client qui renonce les perd, le professionnel qui renonce les rembourse au double. Sauf mention contraire au contrat, une somme versée d'avance par un consommateur est présumée être des arrhes : précisez « acompte » sur votre devis.",
        ],
      },
      {
        heading: "La facture d'acompte est obligatoire",
        paragraphs: [
          "Tout assujetti doit émettre une facture pour les acomptes perçus avant la réalisation de l'opération. Elle porte les mêmes mentions qu'une facture ordinaire, avec un numéro pris dans la même séquence, et indique le montant de l'acompte et la TVA correspondante.",
        ],
      },
      {
        heading: 'La TVA sur un acompte',
        paragraphs: [
          "Pour les prestations de services, la TVA est exigible à l'encaissement : l'acompte la déclenche donc pour son montant. Pour les livraisons de biens, c'est le cas aussi depuis le 1er janvier 2023, dès lors que les biens sont désignés avec précision au moment du versement. En franchise en base de TVA, la facture d'acompte est simplement établie hors taxes, avec la mention 293 B.",
        ],
      },
      {
        heading: 'La facture finale',
        paragraphs: [
          "Une fois l'opération réalisée, la facture finale porte sur le prix total et fait référence aux factures d'acompte déjà émises (numéro et date), en indiquant le montant restant à payer. La TVA déjà déclarée sur l'acompte n'est pas déclarée une seconde fois.",
          "Avec la facturation électronique entre professionnels, certaines plateformes n'acceptent pas de ligne négative : l'acompte se régularise alors par un avoir rattaché à la facture d'acompte, puis la facture finale est émise pour le total.",
        ],
      },
    ],
    faq: [
      [
        "Un acompte payé par carte change-t-il quelque chose ?",
        "Non, seul l'encaissement compte : la facture d'acompte est due dès réception du paiement, quel que soit le moyen. Elle peut être générée automatiquement au moment du paiement.",
      ],
      [
        "Quel montant d'acompte demander ?",
        "Il est libre et se négocie, souvent entre 20 et 40 % pour des travaux. Il doit figurer sur le devis accepté. Certains secteurs encadrent les versements avant livraison : vérifiez les règles du vôtre.",
      ],
    ],
    sources: [OFFICIAL.tvaAcompte, OFFICIAL.factureAcompte, OFFICIAL.mentions],
  },
  {
    slug: 'facture-particulier',
    seoTitle: 'Facturer un particulier : obligations et e-reporting',
    seoDescription: "Facture à un client particulier : quand elle est obligatoire, ses mentions, l'envoi hors facture électronique et la transmission des données (e-reporting).",
    title: 'Facturer un client particulier : obligations, mentions et e-reporting',
    shortTitle: 'Facturer un particulier',
    description:
      "Quand faire une facture à un particulier, les mentions propres aux consommateurs, pourquoi elle ne passe pas par la facture électronique, et ce que change le e-reporting.",
    updatedOn: '2026-10-27',
    summary:
      "Une facture est obligatoire pour un particulier s'il la demande, pour toute prestation de services de plus de 25 € TTC et pour les travaux dans un logement. Ces factures ne passent pas par le réseau de la facture électronique : vous les remettez sur papier ou par email, et leurs montants sont transmis à l'administration par le e-reporting, aux mêmes échéances que la réforme.",
    sections: [
      {
        heading: 'Quand la facture est obligatoire',
        list: [
          'Quand le client la demande.',
          'Pour une prestation de services dont le prix dépasse 25 € TTC (une note doit alors lui être remise).',
          'Pour des travaux immobiliers et pour les ventes à distance.',
        ],
        paragraphs: ["Dans les autres cas (vente au comptoir d'un faible montant), un ticket de caisse peut suffire."],
      },
      {
        heading: 'Les mentions',
        paragraphs: [
          "Une facture à un particulier reprend les mentions habituelles (numéro, date, identité du vendeur, désignation, prix, TVA), sans exiger de SIRET ni de numéro de TVA pour le client : son nom et son adresse suffisent.",
          "Les mentions propres aux relations entre professionnels ne s'appliquent pas : pas d'indemnité forfaitaire de 40 € pour frais de recouvrement, et les pénalités de retard ne sont dues que si elles ont été prévues au contrat.",
        ],
      },
      {
        heading: 'Pas de facture électronique, mais du e-reporting',
        paragraphs: [
          "L'obligation de facture électronique ne vise que les échanges entre entreprises assujetties à la TVA en France. Pour un particulier, vous continuez d'envoyer la facture par email, en PDF, ou de la remettre en main propre.",
          "En revanche, les données de ces ventes (montants, TVA, date) devront être transmises à l'administration : c'est le e-reporting, obligatoire au même calendrier que la réforme, soit à partir de septembre 2027 pour les PME, TPE et micro-entreprises. Il se fait de façon agrégée, par votre plateforme agréée, sans transmettre le détail de chaque client.",
        ],
      },
    ],
    faq: [
      [
        "Puis-je envoyer la facture d'un particulier par email ?",
        'Oui, en PDF. Gardez-en une copie : vous devez conserver vos factures pendant dix ans.',
      ],
      [
        'Un même client peut-il être particulier puis professionnel ?',
        "Oui : ce qui compte, c'est la qualité dans laquelle il achète. Un artisan qui commande pour son entreprise est un client professionnel ; la facture passe alors par la facture électronique à votre échéance.",
      ],
    ],
    sources: [OFFICIAL.mentions, OFFICIAL.reform, OFFICIAL.economie],
  },
  {
    slug: 'facture-impayee-relance-penalites',
    seoTitle: 'Facture impayée : délais, pénalités et relances',
    seoDescription: 'Délais de paiement entre professionnels, pénalités de retard, indemnité de 40 € et étapes de relance d’une facture impayée, jusqu’à l’injonction de payer.',
    title: 'Facture impayée : délais de paiement, pénalités de retard et relances',
    shortTitle: 'Relancer une facture impayée',
    description:
      "Les délais de paiement entre professionnels, le calcul des pénalités de retard, l'indemnité forfaitaire de 40 €, et les étapes pour relancer puis recouvrer une facture impayée.",
    updatedOn: '2026-10-27',
    summary:
      "Entre professionnels, une facture se paie à 30 jours après réception de la prestation, sauf accord, sans dépasser 60 jours après son émission. Dès le premier jour de retard, des pénalités sont dues de plein droit, ainsi qu'une indemnité forfaitaire de 40 € pour frais de recouvrement : relancez tôt, puis mettez en demeure avant d'envisager une injonction de payer.",
    sections: [
      {
        heading: 'Les délais de paiement',
        list: [
          "À défaut d'accord : 30 jours après la réception des marchandises ou l'exécution de la prestation.",
          "Délai maximal convenu : 60 jours après la date d'émission de la facture, ou 45 jours fin de mois si le contrat le prévoit.",
          'Certains secteurs ont des délais spécifiques (transport, denrées périssables…).',
        ],
      },
      {
        heading: 'Pénalités et indemnité de 40 €',
        paragraphs: [
          "Les pénalités de retard sont dues sans rappel, dès le lendemain de l'échéance. Faute de taux prévu au contrat, c'est le taux de refinancement de la Banque centrale européenne majoré de 10 points ; un taux contractuel ne peut être inférieur à trois fois le taux d'intérêt légal.",
          "S'y ajoute une indemnité forfaitaire de 40 € pour frais de recouvrement, due une fois par facture quelle que soit la durée du retard, et non soumise à la TVA. Ces deux éléments doivent figurer sur vos factures et vos conditions générales ; leur absence est sanctionnée par une amende administrative.",
          "Ces règles ne valent qu'entre professionnels : pour un particulier, les pénalités ne sont dues que si le contrat les prévoit, et l'indemnité de 40 € ne s'applique pas.",
        ],
      },
      {
        heading: 'Les étapes de la relance',
        list: [
          "Avant l'échéance : un rappel courtois quelques jours avant évite bien des retards.",
          "Juste après l'échéance : une relance par email avec la facture jointe et les moyens de paiement.",
          'Une à deux semaines plus tard : une seconde relance, plus ferme, rappelant les pénalités et l’indemnité.',
          'Puis une mise en demeure par lettre recommandée, qui fixe un dernier délai.',
          "Enfin, une procédure : injonction de payer auprès du tribunal, ou procédure simplifiée de recouvrement des petites créances par un commissaire de justice (jusqu'à 5 000 €).",
        ],
      },
    ],
    faq: [
      [
        'Dois-je réclamer les pénalités pour qu’elles soient dues ?',
        "Non, elles sont dues de plein droit entre professionnels. Rien ne vous oblige pour autant à les exiger : beaucoup d'entreprises les rappellent sans les facturer, pour préserver la relation.",
      ],
      [
        'Les relances automatiques sont-elles efficaces ?',
        "Oui : un rappel envoyé au bon moment, poli et accompagné de la facture, suffit dans la plupart des cas. L'important est la régularité, sans harceler : une relance par client et par jour au plus.",
      ],
    ],
    sources: [OFFICIAL.delais, OFFICIAL.delaisDgccrf, OFFICIAL.mentions],
  },
  {
    slug: 'fec-comptabilite-micro-entreprise',
    seoTitle: 'FEC, livre des recettes : la comptabilité d’un indépendant',
    seoDescription: 'Fichier des écritures comptables (FEC), livre des recettes et registre des achats : ce que doit tenir un micro-entrepreneur ou une TPE au réel.',
    title: 'FEC, livre des recettes, registre des achats : quelle comptabilité tenir',
    shortTitle: 'FEC et livre des recettes',
    description:
      "Ce qu'est le fichier des écritures comptables (FEC), qui doit le produire, et ce que tient à la place un micro-entrepreneur : livre des recettes et registre des achats.",
    updatedOn: '2026-10-27',
    summary:
      "Le FEC est le fichier normé que doit pouvoir remettre au fisc toute entreprise tenant une comptabilité informatisée, en cas de contrôle. Un micro-entrepreneur, qui n'a pas de comptabilité complète à tenir, doit surtout tenir un livre des recettes (et un registre des achats s'il vend des marchandises) ; un export FEC reste utile pour le transmettre à un comptable.",
    sections: [
      {
        heading: 'Le FEC en bref',
        paragraphs: [
          "Le fichier des écritures comptables reprend, dans un format fixé par l'administration, toutes les écritures d'un exercice : date, journal, compte, libellé, montants au débit et au crédit, pièce justificative. Il est exigé lors d'un contrôle fiscal des entreprises qui tiennent leur comptabilité avec un logiciel ; son absence ou sa non-conformité est sanctionnée par une amende.",
        ],
      },
      {
        heading: 'Le micro-entrepreneur : livre des recettes et registre des achats',
        list: [
          'Un livre des recettes, chronologique : date, client, nature de la prestation ou de la vente, montant, mode de règlement, référence de la pièce.',
          "Un registre des achats, si l'activité est la vente de marchandises ou la fourniture de logement : date, fournisseur, montant, mode de règlement.",
          'Les factures et justificatifs, conservés dix ans.',
        ],
        paragraphs: [
          "Les micro-entrepreneurs sont dispensés de l'examen de comptabilité à distance, qui repose sur le FEC.",
        ],
      },
      {
        heading: 'Au réel : une comptabilité complète',
        paragraphs: [
          "Une entreprise au régime réel tient une comptabilité en partie double (journaux, grand livre, bilan, compte de résultat). Si elle la tient avec un logiciel, elle doit pouvoir produire son FEC. Le plus souvent, c'est l'expert-comptable qui le génère à partir des écritures, d'où l'intérêt d'un export propre de vos ventes et achats.",
        ],
      },
    ],
    faq: [
      [
        'Un tableur suffit-il pour le livre des recettes ?',
        "Oui, si les informations obligatoires y figurent et que les recettes sont inscrites au fil de l'eau. Un outil qui le tient automatiquement à partir de vos factures et encaissements évite les oublis.",
      ],
      [
        'Le livre des recettes enregistre-t-il les factures ou les paiements ?',
        "Les encaissements : c'est le chiffre d'affaires encaissé qui sert de base au micro-entrepreneur, pour ses déclarations comme pour ses seuils.",
      ],
    ],
    sources: [OFFICIAL.fecBofip, OFFICIAL.controleDistance, OFFICIAL.micro],
  },
  {
    slug: 'avoir-facture',
    seoTitle: 'Avoir : corriger ou annuler une facture émise',
    seoDescription: 'Pourquoi une facture émise ne se modifie pas, comment rédiger un avoir, ses mentions et son traitement avec la facture électronique.',
    title: 'Avoir sur facture : comment corriger ou annuler une facture déjà émise',
    shortTitle: 'Corriger une facture : l’avoir',
    description:
      "Une facture émise ne se modifie ni ne se supprime : elle se corrige par un avoir. Mentions, numérotation, TVA et facture électronique, expliqués simplement.",
    updatedOn: '2026-10-27',
    summary:
      "Une facture émise ne peut être ni modifiée ni supprimée : pour la corriger, l'annuler ou accorder une remise après coup, on émet un avoir, c'est-à-dire une facture rectificative qui fait référence à la facture initiale. Avec la facture électronique, l'avoir suit le même chemin que la facture qu'il corrige.",
    sections: [
      {
        heading: 'Pourquoi un avoir',
        paragraphs: [
          "La numérotation des factures doit être continue et sans trou, et une facture émise est une pièce comptable et fiscale. La supprimer ou la réécrire ferait disparaître une trace : la correction passe donc par un nouveau document, l'avoir, qui neutralise tout ou partie de la facture d'origine.",
        ],
      },
      {
        heading: 'Les cas courants',
        list: [
          'Erreur sur la facture (prix, quantité, client, taux de TVA) : avoir total, puis nouvelle facture correcte.',
          'Remise, rabais ou geste commercial accordé après coup : avoir partiel.',
          'Retour de marchandises ou prestation annulée : avoir du montant concerné.',
          "Régularisation d'un acompte, lorsque la facture finale est émise pour le total.",
        ],
      },
      {
        heading: "Les mentions d'un avoir",
        list: [
          'Les mêmes mentions obligatoires qu’une facture, avec un numéro dans une séquence continue.',
          "La référence à la facture initiale : son numéro et sa date.",
          'Les montants corrigés, hors taxes et TVA, présentés en moins.',
        ],
      },
      {
        heading: 'Avoir et facture électronique',
        paragraphs: [
          "Entre professionnels, l'avoir est un document électronique comme un autre : il est transmis par la même voie que la facture qu'il corrige, et le destinataire le reçoit sur sa plateforme. Dans les formats normés, il porte un type de document propre (avoir), distinct de la facture.",
        ],
      },
    ],
    faq: [
      [
        'Puis-je supprimer une facture envoyée par erreur ?',
        'Non. Même envoyée au mauvais client, elle doit être annulée par un avoir total, puis la bonne facture émise.',
      ],
      [
        "Un avoir doit-il être remboursé ?",
        "Pas forcément : il peut être déduit d'une prochaine facture ou remboursé, selon ce qui est convenu avec le client.",
      ],
    ],
    sources: [OFFICIAL.mentions, OFFICIAL.reform],
  },
];

export const guideBySlug = (slug: string): Guide | undefined => GUIDES.find((guide) => guide.slug === slug);
